// firebase/deliveryFunctions.js
import { httpsCallable } from 'firebase/functions';
import { functions } from './config';



export const parseDeliveryImageFn = async (imageBase64, mimeType) => {
  try {
    const callable = httpsCallable(functions, 'parseDeliveryImage');
    const result = await callable({ imageBase64, mimeType });
    return result.data;
  } catch (error) {
    console.error('Error calling parseDeliveryImage:', error);
    throw error;
  }
};

// ============================================================================
// DELIVERY CALLABLE FUNCTIONS (Updated for new backend structure)
// ============================================================================

export const assignDeliveryToMemberFn = async (deliveryId, memberId) => {
  try {
    const callable = httpsCallable(functions, 'assignDeliveryToMember');
    const result = await callable({ deliveryId, memberId });
    return result.data;
  } catch (error) {
    console.error('Error calling assignDeliveryToMember:', error);
    throw error;
  }
}
/**
 * Get bus transport options for a route
 * @param {string} origin - Origin address
 * @param {string} destination - Destination address
 * @param {number|null} departureTime - Unix timestamp (seconds), or null for now
 * @returns {Promise<{startAddress, endAddress, transportOptions[]}>}
 */
export const getBusOptionsFn = async (origin, destination, departureTime) => {

  try {
    const callable = httpsCallable(functions, 'getBusOptions');
    const result = await callable({
      origin,
      destination,
      departure_time: departureTime,
    });
    return result.data;
  } catch (error) {
    console.error('Error calling getBusOptions:', error);
    throw error;
  }
};


/**
 * Create a new delivery
 * 
 * @param {Object} payload - Delivery data with NEW structure
 * @param {string} payload.from - Pickup address string
 * @param {Object} payload.fromInfo - Structured pickup location
 * @param {string} payload.fromInfo.address - Full address
 * @param {number} payload.fromInfo.latitude - Latitude
 * @param {number} payload.fromInfo.longitude - Longitude
 * @param {string} payload.fromInfo.city - City name (optional)
 * @param {string} payload.fromInfo.postalCode - Postal code (optional)
 * @param {Object} payload.fromInfo.details - Apartment, floor, etc.
 * @param {string} payload.to - Delivery address string
 * @param {Object} payload.toInfo - Structured delivery location (same as fromInfo)
 * @param {string} payload.senderPhone - Sender phone number
 * @param {Object} payload.senderInfo - Sender details { name, email }
 * @param {string} payload.receiverPhone - Receiver phone number
 * @param {Object} payload.receiverInfo - Receiver details { name, email }
 * @param {string} payload.pickUpTime - ISO timestamp
 * @param {string} payload.targetTime - ISO timestamp
 * @param {number} payload.price - What customer pays
 * @param {number} payload.reward - What courier gets
 * @param {number} payload.distance - Distance in meters
 * @param {number} payload.duration - Duration in seconds
 * @returns {Promise<{deliveryId: string, success: boolean}>}
 * 
 * @example
 * const result = await createDeliveryFn({
 *   from: "Puijonlaaksontie 2, 70210 Kuopio",
 *   fromInfo: {
 *     address: "Puijonlaaksontie 2, 70210 Kuopio",
 *     city: "Kuopio",
 *     postalCode: "70210",
 *     latitude: 62.897,
 *     longitude: 27.647,
 *     placeId: "ChIJ...",
 *     details: { apartment: "5", floor: "2", instructions: "Ring the bell" }
 *   },
 *   to: "Mannerheimintie 1, 00100 Helsinki",
 *   toInfo: { ... },
 *   senderPhone: "+358401234567",
 *   senderInfo: { name: "John Doe", email: "john@example.com" },
 *   receiverPhone: "+358407654321",
 *   receiverInfo: { name: "Jane Smith", email: "jane@example.com" },
 *   pickUpTime: "2024-02-09T14:00:00.000Z",
 *   targetTime: "2024-02-09T17:00:00.000Z",
 *   price: 25.00,
 *   reward: 17.50,
 *   distance: 35000,  // meters
 *   duration: 2700,   // seconds
 *   deliveryType: "package",
 *   deliveryTransportType: "express",
 *   packageSize: "small",
 *   weightKg: 5,
 *   returnParcels: false,
 *   temperatureControl: false,
 *   canBeLeftAtDoor: true,
 *   billing: {
 *     paymentMethod: "invoice",
 *     yNumber: "1234567-8",
 *     companyName: "Example Oy"
 *   }
 * });
 */
export const createDeliveryFn = async (payload) => {
  try {
    const callable = httpsCallable(functions, 'createDelivery');
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    console.error('Error calling createDelivery:', error);
    throw error;
  }
};

export const acceptDeliveryFn = async (deliveryId) => {
  try {
    const callable = httpsCallable(functions, 'acceptDelivery');
    const result = await callable({ deliveryId });
    return result.data;
  } catch (error) {
    console.error('Error calling acceptDelivery:', error);
    throw error;
  }
};

/**
 * Manually invoice a specific set of deliveries for an organization.
 *
 * @param {string} organizationId - The organization ID
 * @param {string[]} deliveryIds  - Array of delivery document IDs to invoice
 * @returns {Promise<{ success: boolean, invoicesSent: number, invoicesFailed: number, total: number }>}
 */
export const invoiceSelectedDeliveriesFn = async (organizationId, deliveryIds) => {
  try {

      const callable = httpsCallable(functions, 'invoiceSelectedDeliveries');

    const result = await callable({ organizationId, deliveryIds });
    return result.data;
  } catch (error) {
    console.error("Error calling invoiceSelectedDeliveries:", error);
    throw error;
  }
};

/**
 * Revert delivery back to active status
 * 
 * Reverts delivery all the way back to active status and unassigns the courier.
 * Works from both "ongoing" and "picked" statuses.
 * 
 * Status transitions:
 * - "picked" → "active" (unassigns courier)
 * - "ongoing" → "active" (unassigns courier)
 * 
 * Cannot revert from "active" or "completed"
 * 
 * @param {string} deliveryId - The delivery ID
 * @returns {Promise<{success: boolean}>}
 * 
 * @example
 * // Revert delivery to active (from any status)
 * await revertDeliveryFn("DEL-ABC12345");
 */
export const revertDeliveryFn = async (deliveryId) => {
  try {
    const callable = httpsCallable(functions, 'revertDelivery');
    const result = await callable({ deliveryId });
    return result.data;
  } catch (error) {
    console.error('Error calling revertDelivery:', error);
    throw error;
  }
};


/**
 * Permanently delete a delivery (admin function).
 *
 * Removes the delivery document and strips its snapshot from the
 * sender's and courier's deliveries maps. Irreversible.
 *
 * @param {string} deliveryId - The delivery ID
 * @returns {Promise<{success: boolean}>}
 */
export const deleteDeliveryFn = async (deliveryId) => {
  try {
    const callable = httpsCallable(functions, 'deleteDelivery');
    const result = await callable({ deliveryId });
    return result.data;
  } catch (error) {
    console.error('Error calling deleteDelivery:', error);
    throw error;
  }
};

/**
 * Update delivery status
 * 
 * Used to progress delivery through states:
 * - "active" (revert to unassigned)
 * - "ongoing" (courier accepted)
 * - "picked" (courier picked up package)
 * 
 * Note: "completed" should use completeDeliveryFn instead
 * 
 * @param {string} deliveryId - The delivery ID
 * @param {string} newStatus - New status: "active" | "ongoing" | "picked"
 * @returns {Promise<{success: boolean}>}
 * 
 * @example
 * // Mark as picked up
 * await updateDeliveryStatusFn("DEL-ABC12345", "picked");
 * 
 * // Revert to active (unassign courier)
 * await updateDeliveryStatusFn("DEL-ABC12345", "active");
 */
export const updateDeliveryStatusFn = async (deliveryId, newStatus) => {
  try {
    const callable = httpsCallable(functions, 'updateDeliveryStatus');
    const result = await callable({ deliveryId, newStatus });
    return result.data;
  } catch (error) {
    console.error('Error calling updateDeliveryStatus:', error);
    throw error;
  }
};

/**
 * Mark delivery as picked up (convenience wrapper)
 * 
 * @param {string} deliveryId - The delivery ID
 * @returns {Promise<{success: boolean}>}
 */
export const markDeliveryAsPicked = async (deliveryId) => {
  return updateDeliveryStatusFn(deliveryId, 'picked');
};

/**
 * Revert delivery to active state (unassign courier)
 * 
 * @param {string} deliveryId - The delivery ID
 * @returns {Promise<{success: boolean}>}
 */
export const revertDeliveryToActive = async (deliveryId) => {
  return updateDeliveryStatusFn(deliveryId, 'active');
};

/**
 * Complete a delivery
 * 
 * Marks delivery as completed and moves it to completed-deliveries collection.
 * Increments counters for both sender and courier.
 * 
 * @param {string} deliveryId - The delivery ID
 * @returns {Promise<{success: boolean}>}
 * 
 * @example
 * await completeDeliveryFn("DEL-ABC12345");
 */
export const completeDeliveryFn = async (deliveryId) => {
  try {
    const callable = httpsCallable(functions, 'completeDelivery');
    const result = await callable({ deliveryId });
    return result.data;
  } catch (error) {
    console.error('Error calling completeDelivery:', error);
    throw error;
  }
};

/**
 * Ping courier location (real-time tracking)
 * 
 * Should be called periodically (e.g., every 30 seconds) while courier
 * has an active delivery.
 * 
 * @param {string} deliveryId - The delivery ID being tracked
 * @param {Object} location - Location data
 * @param {Object} location.coordinates - GPS coordinates
 * @param {number} location.coordinates.long - Longitude (-180 to 180)
 * @param {number} location.coordinates.lat - Latitude (-90 to 90)
 * @param {string} [location.address] - Optional human-readable address
 * @param {string} [location.city] - Optional city name
 * @returns {Promise<{success: boolean}>}
 * 
 * @example
 * await pingCourierLocationFn("DEL-ABC12345", {
 *   coordinates: { long: 27.647, lat: 62.897 },
 *   address: "Puijonlaaksontie 2, Kuopio",
 *   city: "Kuopio"
 * });
 */
export const pingCourierLocationFn = async (deliveryId, location) => {
  try {
    const callable = httpsCallable(functions, 'pingCourierLocation');
    const result = await callable({ deliveryId, location });
    return result.data;
  } catch (error) {
    console.error('Error calling pingCourierLocation:', error);
    throw error;
  }
};


export const parseDeliveryTextFn = async (text) => {
  try {
    const callable = httpsCallable(functions, 'parseDeliveryText');
    const result = await callable({ text });
    return result.data;
  } catch (error) {
    console.error('Error calling parseDeliveryText:', error);
    throw error;
  }
};

/**
 * Parse delivery info from recorded audio using AI (Speech-to-Text → Gemini).
 *
 * The audio must be sent as a base64-encoded string because httpsCallable
 * only supports JSON payloads (no FormData / multipart).
 *
 * @param {string} audioBase64 - Base64-encoded audio data
 * @param {string} [mimeType='audio/webm'] - MIME type of the audio
 * @returns {Promise<{success: boolean, data: Object, transcript: string}>}
 *
 * @example
 * // Convert Blob → base64 on the client:
 * const reader = new FileReader();
 * reader.onloadend = async () => {
 *   const base64 = reader.result.split(',')[1];
 *   const result = await parseDeliveryAudioFn(base64, 'audio/webm');
 * };
 * reader.readAsDataURL(audioBlob);
 */
export const parseDeliveryAudioFn = async (audioBase64, mimeType = 'audio/webm') => {
  try {
    const callable = httpsCallable(functions, 'parseDeliveryAudio');
    const result = await callable({ audioBase64, mimeType });
    return result.data;
  } catch (error) {
    console.error('Error calling parseDeliveryAudio:', error);
    throw error;
  }
};

/**
 * Update editable fields of a delivery (admin function).
 *
 * Does NOT handle status transitions — use the dedicated functions for that.
 * Updates the delivery document AND the snapshot stored on the sender/courier.
 *
 * @param {string} deliveryId - The delivery ID
 * @param {Object} params     - Fields to update (see DeliveryEdit for the full set)
 * @returns {Promise<{success: boolean}>}
 */
export const updateDeliveryFn = async (deliveryId, params) => {
  try {
    const callable = httpsCallable(functions, 'updateDelivery');
    const result = await callable({ deliveryId, ...params });
    return result.data;
  } catch (error) {
    console.error('Error calling updateDelivery:', error);
    throw error;
  }
};

export const organizationUpdateDeliveryFn = async (data) => {

  try {
    const callable = httpsCallable(functions, 'organizationUpdateDelivery');
    const result = await callable(data);
    return result.data;
  } catch (error) {
    console.error('Error calling organizationUpdateDelivery:', error);
    throw error;
  }
}

// Add to firebase/deliveryFunctions.js

/**
 * Submit a review for a completed delivery.
 *
 * Writes to reviews/{deliveryId} and updates the courier's user doc
 * with reviewed: true, stars, reviewedAt, and optionally dispute: true.
 *
 * @param {string} deliveryId
 * @param {Object} review
 * @param {number} review.stars          - 1–5
 * @param {Object} review.checks         - { origin, destination, times, reward } — true = OK, false = issue
 * @param {Object} [review.notes]        - { origin?, destination?, times?, reward? } — free text if issue
 * @param {boolean} [review.dispute]     - true if any check is false
 * @returns {Promise<{success: boolean}>}
 */
export const submitDeliveryReviewFn = async (deliveryId, review) => {
  try {
    const callable = httpsCallable(functions, 'submitDeliveryReview');
    const result = await callable({ deliveryId, ...review });
    return result.data;
  } catch (error) {
    console.error('Error calling submitDeliveryReview:', error);
    throw error;
  }
};